import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Card, Dialog, DialogTitle, IconButton, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import OrderDetailsCard from './OrderDetailsCard'
import { makeStyles } from 'tss-react/mui'
import OrderStatus from './OrderStatus'
import { J2Multiply, K1Product } from '@e3dc-react/icons'
import MoreDetailsCard from './MoreDetailsCard'
import OrderHistoryProducts from './OrderHistoryProducts'
import { useShop } from '../../hooks'
import { OrderHistoryDetailResponse } from '../../models'
import OrderHistoryProductType from './OrderHistoryProductType'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { ShopMainSkeleton } from '../Skeletons'

export interface ShopOrderHistoryDetailsProps {
  orderId: number
  place: number
  onClose: () => void
}

const useStyles = makeStyles()(theme => ({
  productNumberWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  productOrderContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  productNumberContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  productNumberIcon: {
    marginRight: theme.spacing(1),
  },
  orderDetailsWrapper: {
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper2,
  },
  orderDetailsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    flexWrap: 'nowrap',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  button: {
    paddingTop: theme.spacing(2),
    /** Change to "flex" to show up back, temporary disabled due missing real data from backend*/
    display: 'none',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  dialogHeader: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.shades.shade04,
    borderRadius: `${theme.shape.borderRadius}px  ${theme.shape.borderRadius}px 0 0`,
    padding: `0 ${theme.space(4)}px`,
    display: 'flex',
    height: '66px',
    alignItems: 'center',
    margin: '0',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0',
    },
  },
  dialogIconButton: {
    flexDirection: 'column',
    color: theme.palette.common.white,
    width: '28px',
    height: '28px',
  },
  dialogRoot: {
    position: 'relative',
    maxWidth: '100%',
    minWidth: '70%',
    minHeight: '50%',
    height: '80%',
    padding: 0,
    margin: 0,
    //to hide scrollbar in FF
    scrollbarWidth: 'none',
    backgroundColor: 'transparent',
    borderRadius: theme.shape.borderRadius,
    //to hide scrollbar in Chrome,Edge and safari
    '&::-webkit-scrollbar': {
      display: 'none',
    },

    [theme.breakpoints.down('sm')]: {
      borderRadius: '0',
      width: '100%',
      height: '100%',
      minHeight: '100%',
      maxWidth: '100%',
    },
  },

  dialogBackground: {
    backgroundColor: theme.palette.grey.grey07,
    borderRadius: `0 0 ${theme.shape.borderRadius}px  ${theme.shape.borderRadius}px`,
    padding: `${theme.space(2)}px`,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0',
      padding: 0,
      height: 'calc(100% - 66px)',
      width: '100%',
      maxWidth: '100%',
    },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    overflowY: 'scroll',
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  failedContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}))

/*
 * This shows the order history details for an already created order
 */
const ShopOrderHistoryDetailsModal: React.FC<ShopOrderHistoryDetailsProps> = ({ orderId, onClose }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [details, setDetails] = useState<OrderHistoryDetailResponse | null>(null)
  const { getOrderHistoryDetails } = useShop()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { cancelProductOrder } = useShop()
  useEffect(() => {
    setIsLoading(true)
    getOrderHistoryDetails(orderId)
      .then(result => {
        setDetails(result)
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCancelOrder = (): void => {
    cancelProductOrder(orderId)
      .then(result => {
        if (!result.hasErrors && !result.value) {
          enqueueErrorStack(t('shop.order-history-card.cancel-order-failed'))
        }
        if (result.value) {
          return enqueueSuccessStack(t('shop.order-history-card.cancel-order-success'))
        }
      })
      .catch(error => {
        console.error(error)
        enqueueErrorStack(t('shop.order-history-card.cancel-order-failed'))
      })
  }

  const wizardHeader = (
    <DialogTitle className={classes.dialogHeader}>
      <Typography variant="h3">{`${orderId} ${t('shop.order-history-card.order-id-title')}`}</Typography>
      <IconButton className={classes.dialogIconButton} onClick={onClose}>
        <J2Multiply />
      </IconButton>
    </DialogTitle>
  )

  const orderDate = details?.sapOrder.orderDate ?? ''
  const referenceNumber = details?.sapOrder.customerReferenceNumber ?? null
  const cancelableUntil = details?.sapOrder.cancellableUntil ?? null
  const productNetPrice = details?.sapOrder.totalNet ?? 0
  const isOrderCancelable = details?.sapOrder.isCancellable
  const orderStatuses = details?.extensions.e3dc.orderStatuses
  const currentStatusType = details?.sapOrder.statusId
  const isStatusAvailable = currentStatusType && orderStatuses
  const desiredDeliveryDate = details?.desiredDeliveryDate ?? ''
  const billingAddress = details?.billingAddress ?? ''
  const shippingAddress = details?.shippingAddress ?? ''
  const customerName = details?.orderer ?? ''
  const productType = details?.consumptionType
  const orderProducts = details?.lineItems ?? []

  return (
    <Dialog classes={{ paper: classes.dialogRoot }} open={true} onClose={onClose}>
      {wizardHeader}

      <Box className={classes.dialogBackground}>
        {isLoading ? (
          <Box>
            <ShopMainSkeleton shopSkeletonType="history" />
          </Box>
        ) : (
          <Box className={classes.cardContainer}>
            {details ? (
              <Card>
                <Box className={classes.headerContainer}>
                  <Box className={classes.productOrderContainer}>
                    <Box className={classes.productNumberContainer}>
                      <K1Product className={classes.productNumberIcon} />
                      <Typography>{`${t('shop.order-history-card.order-id')}: ${orderId}`}</Typography>
                    </Box>
                    {isStatusAvailable && <OrderStatus statusType={currentStatusType} statuses={orderStatuses} />}
                  </Box>
                  <OrderHistoryProductType productType={productType} />
                </Box>
                <Card className={classes.orderDetailsWrapper}>
                  <Box className={classes.orderDetailsContainer}>
                    <OrderDetailsCard
                      orderDate={orderDate}
                      referenceNumber={referenceNumber}
                      cancelableUntil={cancelableUntil}
                      netPrice={productNetPrice}
                    />
                  </Box>
                </Card>
                <MoreDetailsCard
                  billingAddress={billingAddress}
                  shippingAddress={shippingAddress}
                  desiredDeliveryDate={desiredDeliveryDate}
                  customerName={customerName}
                />
                <Box className={classes.productNumberWrapper}>
                  <Typography variant="body1">{t('shop.order-history.products-label')}</Typography>
                </Box>
                {orderProducts
                  .sort((a, b) => a.position - b.position)
                  .map(product => {
                    return (
                      <OrderHistoryProducts
                        key={product.position}
                        name={product.name}
                        quantity={product.quantity}
                        price={product.totalPrice}
                        subProducts={product.children}
                      />
                    )
                  })}
              </Card>
            ) : (
              <Box className={classes.failedContainer}>
                <Typography>{t('shop.order-history-card.failed-loading')}</Typography>
              </Box>
            )}
            <Box className={classes.button}>
              <Button variant="outlined">{t('shop.order-history.last-document-download-label')}</Button>
              {isOrderCancelable && (
                <Button variant="text" onClick={handleCancelOrder}>
                  {t('shop.order-history.cancel-order')}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

export default ShopOrderHistoryDetailsModal
